import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Container,
  Divider,
  Group,
  Modal,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
  Title,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuAnchor, LuArrowLeft, LuDroplets, LuPencil, LuPlus, LuTrash2, LuZap } from 'react-icons/lu'
import { CurrencyText } from '@/frontend/components/marina/CurrencyText'
import { PillNav } from '@/frontend/components/marina/PillNav'
import { StatusBadge } from '@/frontend/components/marina/StatusBadge'
import {
  type Berth,
  type Pontoon,
  useBerths,
  useCreateBerth,
  useCreatePontoon,
  useDeleteBerth,
  useDeletePontoon,
  usePontoons,
  useUpdateBerth,
  useUpdatePontoon,
} from '@/frontend/hooks/useBerths'

export const Route = createFileRoute('/dashboard/settings/berths')({
  component: SettingsBerths,
})

function SettingsBerths() {
  const { t } = useTranslation()
  const { data: pontoonsData } = usePontoons()
  const pontoons = pontoonsData?.pontoons ?? []
  const [filterPontoon, setFilterPontoon] = useState<string | null>(null)

  const { data: berthsData } = useBerths(filterPontoon ? { pontoonId: filterPontoon } : {})
  const berths = berthsData?.items ?? []

  const [pontoonModal, pontoonModalH] = useDisclosure(false)
  const [editingPontoon, setEditingPontoon] = useState<Pontoon | null>(null)
  const [berthModal, berthModalH] = useDisclosure(false)
  const [editingBerth, setEditingBerth] = useState<Berth | null>(null)

  const openEditPontoon = (p: Pontoon) => {
    setEditingPontoon(p)
    pontoonModalH.open()
  }
  const openCreateBerth = () => {
    setEditingBerth(null)
    berthModalH.open()
  }
  const openEditBerth = (b: Berth) => {
    setEditingBerth(b)
    berthModalH.open()
  }

  // Stats for the current filter
  const availableCount = berths.filter((b) => b.status === 'AVAILABLE').length
  const occupiedCount = berths.filter((b) => b.status === 'OCCUPIED' || b.status === 'RESERVED').length
  const maintenanceCount = berths.filter((b) => b.status === 'MAINTENANCE' || b.status === 'BLOCKED').length

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        {/* Header */}
        <Group gap="sm" align="center">
          <Anchor component={Link} to="/dashboard/settings" c="dimmed" style={{ display: 'flex' }}>
            <LuArrowLeft size={18} />
          </Anchor>
          <ThemeIcon variant="light" color="laut" size="lg" radius="md">
            <LuAnchor size={18} />
          </ThemeIcon>
          <div>
            <Title order={2} ff="var(--font-display)" fw={600}>
              {t('page.settings.berths.title')}
            </Title>
            <Text size="xs" c="dimmed">
              {t('page.settings.berths.subtitle')}
            </Text>
          </div>
        </Group>

        {/* Pontoon section */}
        <Card padding="lg" radius="md">
          <Group justify="space-between" mb="md">
            <Text fw={600} size="sm">
              {t('page.settings.berths.pontoon_section', { n: pontoons.length })}
            </Text>
            <Button
              size="xs"
              color="karang"
              leftSection={<LuPlus size={14} />}
              onClick={() => {
                setEditingPontoon(null)
                pontoonModalH.open()
              }}
            >
              {t('page.settings.berths.btn_add_pontoon')}
            </Button>
          </Group>

          {pontoons.length === 0 ? (
            <Text c="dimmed" ta="center" py="md" size="sm">
              {t('page.settings.berths.empty_pontoon')}
            </Text>
          ) : (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('page.settings.berths.col_code')}</Table.Th>
                  <Table.Th>{t('page.settings.berths.col_name')}</Table.Th>
                  <Table.Th ta="right">{t('page.settings.berths.col_berths')}</Table.Th>
                  <Table.Th ta="right">{t('page.settings.berths.col_sort')}</Table.Th>
                  <Table.Th ta="right" />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {pontoons.map((p) => (
                  <Table.Tr key={p.id}>
                    <Table.Td>
                      <Text fw={700} size="sm">
                        {p.code}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{p.name ?? '—'}</Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Badge size="xs" variant="light" color="laut">
                        {p._count?.berths ?? 0}
                      </Badge>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm" c="dimmed">
                        {p.sortOrder}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Group justify="flex-end" gap={4}>
                        <ActionIcon variant="subtle" onClick={() => openEditPontoon(p)} aria-label="Edit">
                          <LuPencil size={14} />
                        </ActionIcon>
                        <DeletePontoonAction pontoon={p} />
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Card>

        {/* Berth section */}
        <Card padding="lg" radius="md">
          <Group justify="space-between" mb="sm">
            <Text fw={600} size="sm">
              {t('page.settings.berths.berth_section')}
            </Text>
            <Button
              size="xs"
              color="karang"
              leftSection={<LuPlus size={14} />}
              onClick={openCreateBerth}
              disabled={pontoons.length === 0}
            >
              {t('page.settings.berths.btn_add_berth')}
            </Button>
          </Group>

          {/* Pontoon filter + stats strip */}
          <Group justify="space-between" mb="md" wrap="wrap" gap="sm">
            <PillNav
              value={filterPontoon ?? 'ALL'}
              onChange={(v) => setFilterPontoon(v === 'ALL' ? null : v)}
              items={[
                {
                  value: 'ALL',
                  label: t('page.settings.berths.filter_all'),
                  count: pontoons.reduce((s, p) => s + (p._count?.berths ?? 0), 0) || undefined,
                  countColor: 'gray',
                },
                ...pontoons.map((p) => ({
                  value: p.id,
                  label: p.code,
                  count: p._count?.berths ?? 0,
                })),
              ]}
            />
            {berths.length > 0 && (
              <Group gap="xs">
                <Badge size="xs" variant="light" color="teal">
                  {t('page.settings.berths.stat_available', { n: availableCount })}
                </Badge>
                {occupiedCount > 0 && (
                  <Badge size="xs" variant="light" color="blue">
                    {t('page.settings.berths.stat_occupied', { n: occupiedCount })}
                  </Badge>
                )}
                {maintenanceCount > 0 && (
                  <Badge size="xs" variant="light" color="orange">
                    {t('page.settings.berths.stat_maintenance', { n: maintenanceCount })}
                  </Badge>
                )}
              </Group>
            )}
          </Group>

          <div style={{ overflowX: 'auto' }}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('page.settings.berths.col_code')}</Table.Th>
                  <Table.Th>Pontoon</Table.Th>
                  <Table.Th>{t('page.settings.berths.col_dimensions')}</Table.Th>
                  <Table.Th>{t('page.settings.berths.col_utilities')}</Table.Th>
                  <Table.Th ta="right">{t('page.settings.berths.col_rate')}</Table.Th>
                  <Table.Th>{t('page.settings.berths.col_status')}</Table.Th>
                  <Table.Th ta="right" />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {berths.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={7}>
                      <Text c="dimmed" ta="center" py="md" size="sm">
                        {filterPontoon
                          ? t('page.settings.berths.empty_berth_filter')
                          : t('page.settings.berths.empty_berth')}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                {berths.map((b) => (
                  <Table.Tr key={b.id} style={{ cursor: 'pointer' }} onClick={() => openEditBerth(b)}>
                    <Table.Td>
                      <Text fw={700} size="sm">
                        {b.code}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{b.pontoon?.code ?? '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{Number(b.maxLoaMeters).toFixed(1)} m LOA</Text>
                      <Text size="xs" c="dimmed">
                        {Number(b.maxBeamMeters).toFixed(1)} m beam · {Number(b.depthMeters).toFixed(1)} m dalam
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        {b.hasWater && (
                          <Badge size="xs" variant="light" color="blue" leftSection={<LuDroplets size={9} />}>
                            Air
                          </Badge>
                        )}
                        {b.hasElectric && (
                          <Badge size="xs" variant="light" color="yellow" leftSection={<LuZap size={9} />}>
                            {b.amperage ? `${b.amperage}A` : 'Listrik'}
                          </Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td ta="right">
                      <CurrencyText idr={b.dailyRateIdr} size="sm" />
                      {b.monthlyRateIdr && (
                        <Text size="xs" c="dimmed">
                          <CurrencyText idr={b.monthlyRateIdr} size="xs" span /> /bln
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <StatusBadge status={b.status} kind="berth" />
                    </Table.Td>
                    <Table.Td ta="right" onClick={(e) => e.stopPropagation()}>
                      <DeleteBerthAction berth={b} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </div>
        </Card>
      </Stack>

      <PontoonModal
        opened={pontoonModal}
        onClose={() => {
          setEditingPontoon(null)
          pontoonModalH.close()
        }}
        pontoon={editingPontoon}
      />
      <BerthModal
        opened={berthModal}
        onClose={() => {
          setEditingBerth(null)
          berthModalH.close()
        }}
        berth={editingBerth}
        pontoons={pontoons}
        defaultPontoonId={filterPontoon}
      />
    </Container>
  )
}

// ─── Pontoon modal ───────────────────────────────────

function PontoonModal({ opened, onClose, pontoon }: { opened: boolean; onClose: () => void; pontoon: Pontoon | null }) {
  const { t } = useTranslation()
  const create = useCreatePontoon()
  const update = useUpdatePontoon()
  const editing = !!pontoon

  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [sortOrder, setSortOrder] = useState(0)

  useMemo(() => {
    if (opened) {
      setCode(pontoon?.code ?? '')
      setName(pontoon?.name ?? '')
      setSortOrder(pontoon?.sortOrder ?? 0)
    }
  }, [opened, pontoon])

  const submit = async () => {
    try {
      if (editing && pontoon) {
        await update.mutateAsync({ id: pontoon.id, data: { code, name: name || null, sortOrder } })
        notifications.show({ title: t('page.settings.berths.notify_pontoon_updated'), message: code, color: 'teal' })
      } else {
        await create.mutateAsync({ code, name: name || null, sortOrder })
        notifications.show({ title: t('page.settings.berths.notify_pontoon_created'), message: code, color: 'teal' })
      }
      onClose()
    } catch (e: unknown) {
      notifications.show({
        title: t('common.error'),
        message: e instanceof Error ? e.message : t('common.try_again'),
        color: 'red',
      })
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        editing
          ? t('page.settings.berths.modal_edit_pontoon', { code: pontoon?.code })
          : t('page.settings.berths.modal_add_pontoon')
      }
    >
      <Stack gap="md">
        <SimpleGrid cols={2}>
          <TextInput
            label={t('page.settings.berths.pontoon_code_label')}
            required
            placeholder="A"
            description={t('page.settings.berths.pontoon_code_desc')}
            value={code}
            onChange={(e) => setCode(e.currentTarget.value)}
          />
          <NumberInput
            label={t('page.settings.berths.pontoon_sort_label')}
            description={t('page.settings.berths.pontoon_sort_desc')}
            value={sortOrder}
            onChange={(v) => setSortOrder(typeof v === 'number' ? v : 0)}
          />
        </SimpleGrid>
        <TextInput
          label={t('page.settings.berths.pontoon_name_label')}
          placeholder="Pontoon A (Transit)"
          description={t('page.settings.berths.pontoon_name_desc')}
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button color="laut" onClick={submit} loading={create.isPending || update.isPending}>
            {editing ? t('page.settings.berths.btn_save') : t('page.settings.berths.btn_create')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

function DeletePontoonAction({ pontoon }: { pontoon: Pontoon }) {
  const { t } = useTranslation()
  const del = useDeletePontoon()
  return (
    <ActionIcon
      variant="subtle"
      color="red"
      aria-label={t('common.delete')}
      onClick={() =>
        modals.openConfirmModal({
          title: t('page.settings.berths.confirm_del_pontoon_title'),
          children: <Text size="sm">{t('page.settings.berths.confirm_del_pontoon_msg', { code: pontoon.code })}</Text>,
          labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
          confirmProps: { color: 'red' },
          onConfirm: async () => {
            try {
              await del.mutateAsync(pontoon.id)
              notifications.show({
                title: t('page.settings.berths.notify_pontoon_deleted'),
                message: pontoon.code,
                color: 'teal',
              })
            } catch (e: unknown) {
              notifications.show({
                title: t('common.error'),
                message: e instanceof Error ? e.message : t('page.settings.berths.err_pontoon_berths'),
                color: 'red',
              })
            }
          },
        })
      }
    >
      <LuTrash2 size={14} />
    </ActionIcon>
  )
}

// ─── Berth modal ─────────────────────────────────────

function BerthModal({
  opened,
  onClose,
  berth,
  pontoons,
  defaultPontoonId,
}: {
  opened: boolean
  onClose: () => void
  berth: Berth | null
  pontoons: Pontoon[]
  defaultPontoonId: string | null
}) {
  const { t } = useTranslation()
  const create = useCreateBerth()
  const update = useUpdateBerth()
  const editing = !!berth

  const BERTH_STATUSES = [
    { value: 'AVAILABLE', label: t('status.berth.AVAILABLE') },
    { value: 'OCCUPIED', label: t('status.berth.OCCUPIED') },
    { value: 'RESERVED', label: t('status.berth.RESERVED') },
    { value: 'MAINTENANCE', label: t('status.berth.MAINTENANCE') },
    { value: 'BLOCKED', label: t('status.berth.BLOCKED') },
  ]

  const [code, setCode] = useState('')
  const [number, setNumber] = useState(1)
  const [pontoonId, setPontoonId] = useState<string>('')
  const [maxLoa, setMaxLoa] = useState(15)
  const [maxBeam, setMaxBeam] = useState(5)
  const [maxDraft, setMaxDraft] = useState(2.5)
  const [depth, setDepth] = useState(3)
  const [hasWater, setHasWater] = useState(true)
  const [hasElectric, setHasElectric] = useState(true)
  const [amperage, setAmperage] = useState<number | null>(32)
  const [dailyRate, setDailyRate] = useState(750_000)
  const [monthlyRate, setMonthlyRate] = useState<number | null>(null)
  const [yearlyRate, setYearlyRate] = useState<number | null>(null)
  const [status, setStatus] = useState<Berth['status']>('AVAILABLE')
  const [notes, setNotes] = useState('')

  useMemo(() => {
    if (opened) {
      setCode(berth?.code ?? '')
      setNumber(berth?.number ?? 1)
      setPontoonId(berth?.pontoonId ?? defaultPontoonId ?? pontoons[0]?.id ?? '')
      setMaxLoa(Number(berth?.maxLoaMeters ?? 15))
      setMaxBeam(Number(berth?.maxBeamMeters ?? 5))
      setMaxDraft(Number(berth?.maxDraftMeters ?? 2.5))
      setDepth(Number(berth?.depthMeters ?? 3))
      setHasWater(berth?.hasWater ?? true)
      setHasElectric(berth?.hasElectric ?? true)
      setAmperage(berth?.amperage ?? 32)
      setDailyRate(Number(berth?.dailyRateIdr ?? 750_000))
      setMonthlyRate(berth?.monthlyRateIdr ? Number(berth.monthlyRateIdr) : null)
      setYearlyRate(berth?.yearlyRateIdr ? Number(berth.yearlyRateIdr) : null)
      setStatus(berth?.status ?? 'AVAILABLE')
      setNotes(berth?.notes ?? '')
    }
  }, [opened, berth, pontoons, defaultPontoonId])

  const submit = async () => {
    const data = {
      code,
      number,
      pontoonId,
      maxLoaMeters: maxLoa,
      maxBeamMeters: maxBeam,
      maxDraftMeters: maxDraft,
      depthMeters: depth,
      hasWater,
      hasElectric,
      amperage,
      dailyRateIdr: dailyRate,
      monthlyRateIdr: monthlyRate,
      yearlyRateIdr: yearlyRate,
      mapRotation: 0,
      status,
      notes: notes || null,
    }
    try {
      if (editing && berth) {
        await update.mutateAsync({ id: berth.id, data })
        notifications.show({ title: t('page.settings.berths.notify_berth_updated'), message: code, color: 'teal' })
      } else {
        await create.mutateAsync(data)
        notifications.show({ title: t('page.settings.berths.notify_berth_created'), message: code, color: 'teal' })
      }
      onClose()
    } catch (e: unknown) {
      notifications.show({
        title: t('common.error'),
        message: e instanceof Error ? e.message : t('common.try_again'),
        color: 'red',
      })
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        editing
          ? t('page.settings.berths.modal_edit_berth', { code: berth?.code })
          : t('page.settings.berths.modal_add_berth')
      }
      size="lg"
    >
      <Stack gap="md">
        {/* Identitas */}
        <SimpleGrid cols={2}>
          <TextInput
            label={t('page.settings.berths.berth_code_label')}
            required
            placeholder="A-12"
            description={t('page.settings.berths.berth_code_desc')}
            value={code}
            onChange={(e) => setCode(e.currentTarget.value)}
          />
          <NumberInput
            label={t('page.settings.berths.berth_number_label')}
            required
            value={number}
            onChange={(v) => setNumber(typeof v === 'number' ? v : 1)}
            min={1}
          />
        </SimpleGrid>
        <Select
          label={t('page.settings.berths.berth_pontoon_label')}
          required
          data={pontoons.map((p) => ({ value: p.id, label: `${p.code}${p.name ? ` — ${p.name}` : ''}` }))}
          value={pontoonId}
          onChange={(v) => setPontoonId(v ?? '')}
        />

        {/* Dimensi */}
        <Divider label={t('page.settings.berths.section_dimensions')} labelPosition="left" />
        <SimpleGrid cols={2}>
          <NumberInput
            label={t('page.settings.berths.berth_loa_label')}
            required
            description={t('page.settings.berths.berth_loa_desc')}
            value={maxLoa}
            onChange={(v) => setMaxLoa(typeof v === 'number' ? v : 0)}
            min={0}
            step={0.5}
            decimalScale={2}
            suffix=" m"
          />
          <NumberInput
            label={t('page.settings.berths.berth_beam_label')}
            required
            description={t('page.settings.berths.berth_beam_desc')}
            value={maxBeam}
            onChange={(v) => setMaxBeam(typeof v === 'number' ? v : 0)}
            min={0}
            step={0.5}
            decimalScale={2}
            suffix=" m"
          />
          <NumberInput
            label={t('page.settings.berths.berth_draft_label')}
            required
            description={t('page.settings.berths.berth_draft_desc')}
            value={maxDraft}
            onChange={(v) => setMaxDraft(typeof v === 'number' ? v : 0)}
            min={0}
            step={0.5}
            decimalScale={2}
            suffix=" m"
          />
          <NumberInput
            label={t('page.settings.berths.berth_depth_label')}
            required
            description={t('page.settings.berths.berth_depth_desc')}
            value={depth}
            onChange={(v) => setDepth(typeof v === 'number' ? v : 0)}
            min={0}
            step={0.5}
            decimalScale={2}
            suffix=" m"
          />
        </SimpleGrid>

        {/* Utilitas */}
        <Divider label={t('page.settings.berths.section_utilities')} labelPosition="left" />
        <Box
          p="sm"
          style={{
            borderRadius: 8,
            background: 'light-dark(var(--mantine-color-gray-0), rgba(255,255,255,0.04))',
          }}
        >
          <Group gap="xl" align="flex-end" wrap="wrap">
            <Switch
              label={t('page.settings.berths.berth_water_label')}
              description={t('page.settings.berths.berth_water_desc')}
              checked={hasWater}
              onChange={(e) => setHasWater(e.currentTarget.checked)}
            />
            <Switch
              label={t('page.settings.berths.berth_electric_label')}
              description={t('page.settings.berths.berth_electric_desc')}
              checked={hasElectric}
              onChange={(e) => setHasElectric(e.currentTarget.checked)}
            />
            {hasElectric && (
              <NumberInput
                label={t('page.settings.berths.berth_amperage_label')}
                placeholder="16 / 32 / 63"
                value={amperage ?? ''}
                onChange={(v) => setAmperage(typeof v === 'number' ? v : null)}
                w={130}
                suffix=" A"
              />
            )}
          </Group>
        </Box>

        {/* Tarif */}
        <Divider label={t('page.settings.berths.section_rates')} labelPosition="left" />
        <SimpleGrid cols={3}>
          <NumberInput
            label={t('page.settings.berths.berth_daily_label')}
            required
            description={t('page.settings.berths.berth_daily_desc')}
            value={dailyRate}
            onChange={(v) => setDailyRate(typeof v === 'number' ? v : 0)}
            min={0}
            step={50_000}
            thousandSeparator=","
          />
          <NumberInput
            label={t('page.settings.berths.berth_monthly_label')}
            description={t('page.settings.berths.berth_monthly_desc')}
            value={monthlyRate ?? ''}
            onChange={(v) => setMonthlyRate(typeof v === 'number' ? v : null)}
            min={0}
            step={500_000}
            thousandSeparator=","
          />
          <NumberInput
            label={t('page.settings.berths.berth_yearly_label')}
            description={t('page.settings.berths.berth_yearly_desc')}
            value={yearlyRate ?? ''}
            onChange={(v) => setYearlyRate(typeof v === 'number' ? v : null)}
            min={0}
            step={5_000_000}
            thousandSeparator=","
          />
        </SimpleGrid>

        {/* Status & Catatan */}
        <Divider label={t('page.settings.berths.section_status')} labelPosition="left" />
        <Select
          label={t('page.settings.berths.berth_status_label')}
          data={BERTH_STATUSES}
          value={status}
          onChange={(v) => setStatus((v ?? 'AVAILABLE') as Berth['status'])}
        />
        <Textarea
          label={t('page.settings.berths.berth_notes_label')}
          description={t('page.settings.berths.berth_notes_desc')}
          rows={2}
          placeholder={t('page.settings.berths.berth_notes_placeholder')}
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
        />

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button color="laut" onClick={submit} loading={create.isPending || update.isPending}>
            {editing ? t('page.settings.berths.btn_save') : t('page.settings.berths.btn_create')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

function DeleteBerthAction({ berth }: { berth: Berth }) {
  const { t } = useTranslation()
  const del = useDeleteBerth()
  return (
    <ActionIcon
      variant="subtle"
      color="red"
      aria-label={t('common.delete')}
      onClick={() =>
        modals.openConfirmModal({
          title: t('page.settings.berths.confirm_del_berth_title'),
          children: <Text size="sm">{t('page.settings.berths.confirm_del_berth_msg', { code: berth.code })}</Text>,
          labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
          confirmProps: { color: 'red' },
          onConfirm: async () => {
            try {
              await del.mutateAsync(berth.id)
              notifications.show({
                title: t('page.settings.berths.notify_berth_deleted'),
                message: berth.code,
                color: 'teal',
              })
            } catch (e: unknown) {
              notifications.show({
                title: t('common.error'),
                message: e instanceof Error ? e.message : t('page.settings.berths.err_berth_bookings'),
                color: 'red',
              })
            }
          },
        })
      }
    >
      <LuTrash2 size={14} />
    </ActionIcon>
  )
}
